import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GraduationCap, Users, BookOpen, Shield } from 'lucide-react';
import { useLevels } from '@/hooks/useLevels';
import { usePublicTenants } from '@/hooks/usePublicTenants';
import ForgotPassword from './ForgotPassword';
import { LoadingButton } from '@/components/ui/loading-button';
import { ErrorAlert } from '@/components/ui/error-alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = ({ onLogin, onRegister }) => {
  const { enrollmentCode } = useParams(); // Récupérer le code depuis l'URL
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(enrollmentCode ? 'register' : 'login'); // Si code présent, aller directement à l'inscription
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // États pour les erreurs
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  
  // États pour la sélection de tenant (multi-comptes)
  const [showTenantSelection, setShowTenantSelection] = useState(false);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    invitationToken: '',
    level: '',
    enrollmentCode: '',  // Code d'inscription
    tenantId: '',  // Sera rempli après validation du code
    tenantName: ''  // Nom de l'établissement après validation
  });
  // Utiliser les hooks personnalisés
  const { levels, loading: levelsLoading } = useLevels();
  
  // État pour la validation du code
  const [codeValidating, setCodeValidating] = useState(false);
  const [codeValidated, setCodeValidated] = useState(false);
  
  const [isInvitationMode, setIsInvitationMode] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState(null);

  // Effet pour gérer le code d'inscription depuis l'URL
  useEffect(() => {
    if (enrollmentCode) {
      setRegisterForm(prev => ({ ...prev, enrollmentCode: enrollmentCode.toUpperCase() }));
      // Valider automatiquement le code si présent dans l'URL
      validateEnrollmentCode(enrollmentCode);
    }
  }, [enrollmentCode]);

  // Gestion mot de passe oublié
  if (showForgotPassword) {
    return <ForgotPassword onBackToLogin={() => setShowForgotPassword(false)} />;
  }

  // Fonction pour valider le code d'inscription
  const validateEnrollmentCode = async (code) => {
    if (!code || code.trim().length === 0) {
      setCodeValidated(false);
      setRegisterForm(prev => ({ ...prev, tenantId: '', tenantName: '' }));
      return;
    }

    setCodeValidating(true);
    setRegisterError('');
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/tenant/validate-code/${code.trim().toUpperCase()}`);
      
      // Code valide
      setCodeValidated(true);
      setRegisterForm(prev => ({
        ...prev,
        tenantId: response.data.tenant_id,
        tenantName: response.data.tenant_name
      }));
      toast.success(`Établissement trouvé : ${response.data.tenant_name}`);
    } catch (error) {
      setCodeValidated(false);
      setRegisterForm(prev => ({ ...prev, tenantId: '', tenantName: '' }));
      
      if (error.response?.status === 404) {
        setRegisterError('Code d\'inscription invalide. Vérifiez le code fourni par votre établissement.');
      } else if (error.response?.status === 403) {
        setRegisterError('Les inscriptions sont fermées pour cet établissement.');
      } else {
        setRegisterError('Erreur lors de la validation du code. Réessayez.');
      }
    } finally {
      setCodeValidating(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setLoginError(''); // Réinitialiser l'erreur précédente
    
    try {
      const result = await onLogin(loginForm.email, loginForm.password);
      
      // Vérifier si plusieurs comptes trouvés
      if (result && result.multipleAccounts) {
        setTenantOptions(result.tenantOptions);
        const remembered = localStorage.getItem(`last_tenant_${loginForm.email.toLowerCase()}`);
        if (remembered && result.tenantOptions.some((o) => o.tenant_id === remembered)) setSelectedTenant(remembered);
        setShowTenantSelection(true);
        setLoginError('');
        setIsLoading(false);
        return;
      }
      
      // Vérifier si la connexion a échoué
      if (result && !result.success) {
        // La fonction onLogin retourne un objet avec success: false
        if (result.error) {
          if (result.error.includes('Invalid credentials') || result.error.includes('credential')) {
            setLoginError('Email ou mot de passe incorrect');
          } else if (result.error.includes('not found') || result.error.includes('trouvé')) {
            setLoginError('Utilisateur non trouvé');
          } else if (result.error.includes('disabled') || result.error.includes('désactivé')) {
            setLoginError('Compte désactivé. Contactez l\'administration');
          } else {
            setLoginError(result.error);
          }
        } else {
          setLoginError('Email ou mot de passe incorrect');
        }
        


      }
      // Si result.success est true, la connexion a réussi et l'utilisateur sera redirigé automatiquement
      
    } catch (error) {
      // Cette partie gère les erreurs techniques (réseau, etc.)
      console.error('Login failed:', error);
      setLoginError('Erreur de connexion. Veuillez réessayer');
      toast.error('Erreur technique');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginWithTenant = async () => {
    if (!selectedTenant) {
      setLoginError('Veuillez sélectionner un établissement');
      return;
    }
    
    setIsLoading(true);
    setLoginError('');
    
    try {
      const result = await onLogin(loginForm.email, loginForm.password, selectedTenant);
      if (!result || result.success !== false) {
        localStorage.setItem(`last_tenant_${loginForm.email.toLowerCase()}`, selectedTenant);
      }
      
      if (result && !result.success) {
        setLoginError(result.error || 'Erreur de connexion');

      }
    } catch (error) {
      console.error('Login with tenant failed:', error);
      setLoginError('Erreur de connexion. Veuillez réessayer');
      toast.error('Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError(''); // Réinitialiser l'erreur précédente
    
    // Validation
    if (registerForm.password !== registerForm.confirmPassword) {
      setRegisterError('Les mots de passe ne correspondent pas');
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    // Vérifier qu'un établissement est sélectionné (sauf en mode invitation)
    if (!isInvitationMode && !registerForm.tenantId) {
      setRegisterError('Veuillez entrer et valider votre code d\'inscription');
      return;
    }
    
    if (!isInvitationMode && !codeValidated) {
      setRegisterError('Veuillez valider votre code d\'inscription');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const userData = {
        email: registerForm.email,
        password: registerForm.password,
        first_name: registerForm.firstName,
        last_name: registerForm.lastName,
        phone: registerForm.phone || null,
        level: registerForm.level || null,
        tenant_id: registerForm.tenantId  // Inclure l'établissement choisi
      };
      
      // Si c'est un mode invitation, ajouter le token
      if (isInvitationMode && registerForm.invitationToken) {
        userData.invitation_token = registerForm.invitationToken;
        await onRegister(userData, true); // true pour invitation
      } else {
        await onRegister(userData, false); // false pour inscription normale
      }
    } catch (error) {
      console.error('Registration failed:', error);
      
      // Gestion des différents types d'erreurs d'inscription
      if (error.response?.status === 400) {
        if (error.response.data?.detail?.includes('déjà enregistré') || error.response.data?.detail?.includes('already exists')) {
          setRegisterError('Cette adresse email est déjà utilisée');
        } else if (error.response.data?.detail?.includes('établissement') || error.response.data?.detail?.includes('tenant')) {
          setRegisterError('Problème avec l\'établissement sélectionné');
        } else {
          setRegisterError(error.response.data?.detail || 'Données invalides');
        }
      } else if (error.response?.data?.detail) {
        setRegisterError(error.response.data.detail);
      } else if (error.message) {
        setRegisterError(`Erreur d'inscription: ${error.message}`);
      } else {
        setRegisterError('Erreur lors de la création du compte. Veuillez réessayer');
      }
      
      // Toast pour feedback immédiat
      toast.error('Inscription échouée');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvitationToken = async () => {
    if (!registerForm.invitationToken) {
      alert('Veuillez entrer un token d\'invitation');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/invitations/verify/${registerForm.invitationToken}`);
      setInvitationInfo(response.data);
      setRegisterForm({ ...registerForm, email: response.data.email });
      setIsInvitationMode(true);
    } catch (error) {
      alert('Token d\'invitation invalide ou expiré');
      console.error('Invitation verification failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const roles = [
    { value: 'STUDENT', label: 'Élève', icon: GraduationCap },
    { value: 'PARENT', label: 'Parent', icon: Users },
    { value: 'TEACHER', label: 'Enseignant(e)', icon: BookOpen },
    { value: 'ADMIN', label: 'Administrateur', icon: Shield },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 gradient-header">
      <div className="w-full max-w-md fade-in">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            <div className="bg-white p-3 rounded-full shadow-lg" data-testid="login-eduscope-logo">
              <GraduationCap className="w-8 h-8 text-sky-600" aria-label="Logo EduScope" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">EduScope</h1>
          <p className="text-blue-100">Système de gestion scolaire moderne</p>
        </div>

        {/* Login/Register Card */}
        <Card className="card-large">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger data-testid="login-tab" value="login">Connexion</TabsTrigger>
              <TabsTrigger data-testid="register-tab" value="register">Inscription</TabsTrigger>
            </TabsList>
            
            {/* Login Tab */}
            <TabsContent value="login">
              <CardHeader>
                <CardTitle>Se connecter</CardTitle>
                <CardDescription>
                  Entrez vos identifiants pour accéder à votre espace
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Message d'erreur de connexion */}
                {loginError && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium">{loginError}</span>
                    </div>
                    <button
                      onClick={() => setLoginError('')}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}
                
                {/* Sélection de tenant (si plusieurs comptes trouvés) */}
                {showTenantSelection && tenantOptions.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-blue-900 mb-3">
                      Plusieurs comptes trouvés avec cet email
                    </h3>
                    <p className="text-sm text-blue-800 mb-4">
                      Veuillez sélectionner votre établissement pour continuer :
                    </p>
                    
                    <Select onValueChange={setSelectedTenant} value={selectedTenant}>
                      <SelectTrigger className="mb-4">
                        <SelectValue placeholder="Choisir un établissement" />
                      </SelectTrigger>
                      <SelectContent>
                        {tenantOptions.map((option) => (
                          <SelectItem key={option.tenant_id} value={option.tenant_id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{option.tenant_name}</span>
                              <span className="text-xs text-gray-500">Rôle : {option.user_role}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <div className="flex space-x-2">
                      <Button 
                        onClick={handleLoginWithTenant}
                        disabled={!selectedTenant || isLoading}
                        className="flex-1"
                      >
                        {isLoading ? 'Connexion...' : 'Se connecter'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setShowTenantSelection(false);
                          setSelectedTenant('');
                          setTenantOptions([]);
                        }}
                        disabled={isLoading}
                      >
                        Retour
                      </Button>
                    </div>
                  </div>
                )}
                
                {!showTenantSelection && (
                  <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="votre.email@ecole.fr"
                      value={loginForm.email}
                      onChange={(e) => {
                        setLoginForm({ ...loginForm, email: e.target.value });
                        if (loginError) setLoginError(''); // Effacer l'erreur quand l'utilisateur tape
                      }}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <PasswordInput
                      id="login-password"
                      data-testid="login-password-input"
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => {
                        setLoginForm({ ...loginForm, password: e.target.value });
                        if (loginError) setLoginError(''); // Effacer l'erreur quand l'utilisateur tape
                      }}
                      required
                    />
                  </div>
                  
                  <LoadingButton 
                    data-testid="login-submit-btn"
                    type="submit" 
                    className="w-full btn-primary"
                    loading={isLoading}
                    loadingText="Connexion..."
                  >
                    Se connecter
                  </LoadingButton>
                  
                  {/* Lien mot de passe oublié */}
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </form>
                )}
              </CardContent>
            </TabsContent>
            
            {/* Register Tab */}
            <TabsContent value="register">
              <CardHeader>
                <CardTitle>Créer un compte</CardTitle>
                <CardDescription>
                  Inscrivez-vous pour rejoindre votre établissement
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Message d'erreur d'inscription */}
                {registerError && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium">{registerError}</span>
                    </div>
                    <button
                      onClick={() => setRegisterError('')}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}
                
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Prénom</Label>
                      <Input
                        id="firstName"
                        data-testid="register-firstname-input"
                        placeholder="Prénom"
                        value={registerForm.firstName}
                        onChange={(e) => {
                          setRegisterForm({ ...registerForm, firstName: e.target.value });
                          if (registerError) setRegisterError(''); // Effacer l'erreur
                        }}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Nom</Label>
                      <Input
                        id="lastName"
                        data-testid="register-lastname-input"
                        placeholder="Nom"
                        value={registerForm.lastName}
                        onChange={(e) => setRegisterForm({ ...registerForm, lastName: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email-input"
                      type="email"
                      placeholder="votre.email@ecole.fr"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone (optionnel)</Label>
                    <Input
                      id="phone"
                      data-testid="register-phone-input"
                      type="tel"
                      placeholder="+224 6XX XX XX XX"
                      value={registerForm.phone}
                      onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                    />
                  </div>

                  {!isInvitationMode && (
                    <>
                      {/* Code d'inscription */}
                      <div className="space-y-2">
                        <Label htmlFor="enrollmentCode">Code d'inscription *</Label>
                        <div className="flex gap-2">
                          <Input
                            id="enrollmentCode"
                            data-testid="register-code-input"
                            placeholder="Ex: LBD-1234"
                            value={registerForm.enrollmentCode}
                            onChange={(e) => {
                              const code = e.target.value.toUpperCase();
                              setRegisterForm({ ...registerForm, enrollmentCode: code });
                              setCodeValidated(false);
                              if (registerError) setRegisterError('');
                            }}
                            className={codeValidated ? 'border-green-500' : ''}
                            disabled={codeValidating}
                            required
                          />
                          <Button
                            type="button"
                            onClick={() => validateEnrollmentCode(registerForm.enrollmentCode)}
                            disabled={!registerForm.enrollmentCode || codeValidating || codeValidated}
                            className={codeValidated ? 'bg-green-600 hover:bg-green-700' : ''}
                          >
                            {codeValidating ? (
                              <>
                                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Validation...
                              </>
                            ) : codeValidated ? (
                              <>
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Validé
                              </>
                            ) : (
                              'Valider'
                            )}
                          </Button>
                        </div>
                        
                        {/* Affichage de l'établissement validé */}
                        {codeValidated && registerForm.tenantName && (
                          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                            <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-green-900">{registerForm.tenantName}</p>
                              <p className="text-xs text-green-700">Établissement confirmé</p>
                            </div>
                          </div>
                        )}
                        
                        <p className="text-xs text-gray-600">
                          Entrez le code fourni par votre établissement (ex: LBD-1234)
                        </p>
                      </div>

                      {/* Sélection du niveau d'études */}
                      <div className="space-y-2">
                        <Label htmlFor="level">Niveau d'études *</Label>
                        <Select 
                          value={registerForm.level} 
                          onValueChange={(value) => setRegisterForm({ ...registerForm, level: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez votre niveau d'études" />
                          </SelectTrigger>
                          <SelectContent className="max-h-64 overflow-y-auto">
                            {levelsLoading ? (
                              <SelectItem value="loading" disabled>
                                Chargement des niveaux...
                              </SelectItem>
                            ) : levels.length > 0 ? (
                              levels.map((levelInfo) => (
                                <SelectItem key={levelInfo.level} value={levelInfo.level}>
                                  {levelInfo.label}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="no-levels" disabled>
                                Aucun niveau disponible. Contactez l'administration.
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  
                  {!isInvitationMode && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Shield className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-800">Inscription Élève</span>
                      </div>
                      <p className="text-sm text-blue-700">
                        L'inscription publique est réservée aux élèves. Pour les autres rôles (enseignant, parent, administration), 
                        vous devez avoir reçu une invitation.
                      </p>
                    </div>
                  )}
                  
                  {invitationInfo && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Shield className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">Invitation Validée</span>
                      </div>
                      <p className="text-sm text-green-700">
                        Rôle: <strong>{invitationInfo.role}</strong>
                      </p>
                      {invitationInfo.message && (
                        <p className="text-sm text-green-700 mt-1">
                          Message: {invitationInfo.message}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {!isInvitationMode && (
                    <div className="space-y-2">
                      <Label htmlFor="invitation_token">Code d'invitation (optionnel)</Label>
                      <div className="flex space-x-2">
                        <Input
                          id="invitation_token"
                          value={registerForm.invitationToken}
                          onChange={(e) => setRegisterForm({ ...registerForm, invitationToken: e.target.value })}
                          placeholder="Entrez votre code à 6 chiffres"
                          maxLength={6}
                          pattern="[0-9]{6}"
                        />
                        <LoadingButton
                          type="button"
                          onClick={handleInvitationToken}
                          disabled={!registerForm.invitationToken}
                          loading={isLoading}
                          loadingText="Vérification..."
                          className="btn-secondary"
                        >
                          Vérifier
                        </LoadingButton>
                      </div>
                      <p className="text-xs text-gray-500">
                        Si vous avez reçu un code d'invitation à 6 chiffres pour un autre rôle, entrez-le ici
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Mot de passe</Label>
                    <PasswordInput
                      id="register-password"
                      data-testid="register-password-input"
                      placeholder="••••••••"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                    <PasswordInput
                      id="confirmPassword"
                      data-testid="register-confirm-password-input"
                      placeholder="••••••••"
                      value={registerForm.confirmPassword}
                      onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                      required
                    />
                  </div>
                  
                  <LoadingButton 
                    data-testid="register-submit-btn"
                    type="submit" 
                    className="w-full btn-secondary"
                    loading={isLoading}
                    loadingText="Création du compte..."
                  >
                    Créer mon compte
                  </LoadingButton>
                </form>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>
        
        {/* Footer */}
        <div className="text-center mt-6 text-blue-100 text-sm">
          <p data-testid="login-footer-copyright">© {new Date().getFullYear()} EduScope - Gestion scolaire moderne</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
